/**
 * Todos pane — the open/close right pane inside a Chat/Room.
 *
 * Same rendering and the same completion paths as the focused Todos page; the
 * only difference is `areaId` in the scope. Rows come from `TodoRow`, which is
 * the single implementation of both — see `todo-row.tsx`.
 *
 * The shell (backdrop, width, header, close button, Escape) is deliberately
 * identical to `ThreadSidePanel`, because from the user's side these are the
 * same drawer showing different things.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TodoItem } from '@goodchat/shared/types';
import { fetchTodos, createTask } from '@/lib/api';
import {
  useTodosStore,
  useTodosView,
  useTodosRevision,
  todosFetchParams,
  todosViewKey,
  type TodosScope,
} from '@/stores/todos.store';
import { useAuthStore } from '@/stores/auth.store';
import { describeTaskAudience } from '@/lib/todos-format';
import { TodoRow } from '@/components/todos/todo-row';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { ListSkeleton } from '@/components/ui/skeleton';
import { toast } from '@/stores/toast.store';

const PAGE_SIZE = 25;

export interface TodosPanelProps {
  orgId: string;
  areaId: string;
  areaName: string;
  onClose: () => void;
  /**
   * Optional "take me to it". Left unwired the row body is simply not
   * clickable, which is fine — every action stays reachable in the pane.
   */
  onOpenItem?: (item: TodoItem) => void;
}

export function TodosPanel({ orgId, areaId, areaName, onClose, onOpenItem }: TodosPanelProps) {
  const [status, setStatus] = useState<'open' | 'done'>('open');
  const [addOpen, setAddOpen] = useState(false);

  const currentUserId = useAuthStore((s) => s.user?.id);
  const orgs = useAuthStore((s) => s.orgs);
  const org = useMemo(() => orgs.find((o) => o.id === orgId), [orgs, orgId]);
  const canAdminOrg = org?.role === 'owner' || org?.role === 'admin';

  // A fresh literal every render is fine — only the derived key is load-bearing.
  const scope: TodosScope = { orgId, areaId, status };
  const scopeKey = todosViewKey(scope);
  const view = useTodosView(scope);
  const revision = useTodosRevision();

  // `todos.update` only tells us the counts moved, never which row — so a
  // mounted pane refetches its page when the revision bumps.
  useEffect(() => {
    if (!orgId || !areaId) return;
    let cancelled = false;
    const store = useTodosStore.getState();
    store.beginLoad(scope, 'replace');
    fetchTodos({ ...todosFetchParams(scope), limit: PAGE_SIZE })
      .then((res) => {
        if (!cancelled) useTodosStore.getState().setPage(scope, res);
      })
      .catch(() => {
        if (!cancelled) useTodosStore.getState().setError(scope, "Couldn't load todos");
      });
    return () => {
      cancelled = true;
    };
    // `scope` is rebuilt every render; `scopeKey` is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, revision, orgId, areaId]);

  const handleLoadMore = useCallback(() => {
    if (!view.hasMore || view.loadingMore || !view.nextCursor) return;
    const store = useTodosStore.getState();
    store.beginLoad(scope, 'more');
    fetchTodos({ ...todosFetchParams(scope), limit: PAGE_SIZE, cursor: view.nextCursor })
      .then((res) => useTodosStore.getState().appendPage(scope, res))
      .catch(() => {
        useTodosStore.getState().setError(scope, "Couldn't load more todos");
        toast.error('Failed to load more todos');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey, view.hasMore, view.loadingMore, view.nextCursor]);

  const handleRetry = useCallback(() => {
    const store = useTodosStore.getState();
    store.beginLoad(scope, 'replace');
    fetchTodos({ ...todosFetchParams(scope), limit: PAGE_SIZE })
      .then((res) => useTodosStore.getState().setPage(scope, res))
      .catch(() => useTodosStore.getState().setError(scope, "Couldn't load todos"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  // Escape closes the pane, mirroring ThreadSidePanel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isEmpty = !view.loading && view.items.length === 0 && !view.error;

  return (
    <>
      {/* Backdrop on mobile */}
      <div className="fixed inset-0 z-40 bg-black/40 sm:hidden" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-white sm:relative sm:inset-auto sm:z-auto sm:w-96 sm:flex-shrink-0 sm:border-l sm:border-gray-200 dark:bg-gray-900 sm:dark:border-gray-700">
        {/* Header */}
        <div className="flex h-10 flex-shrink-0 items-center justify-between border-b border-gray-200 px-3 dark:border-gray-700">
          <div className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              Todos in #{areaName}
            </span>
            {view.counts.total > 0 && (
              <span className="min-w-[18px] flex-shrink-0 rounded-full bg-blue-500 px-1 py-0.5 text-center text-[9px] font-bold text-white">
                {view.counts.total > 99 ? '99+' : view.counts.total}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setAddOpen((v) => !v)}
              className="rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-gray-800"
            >
              {addOpen ? 'Cancel' : 'Add'}
            </button>
            <button
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              aria-label="Close todos panel"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Status filter — same treatment as the thread panel's */}
        <div className="flex gap-1 border-b border-gray-200 px-3 py-1.5 dark:border-gray-700">
          <button
            onClick={() => setStatus('open')}
            className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              status === 'open'
                ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="9" />
            </svg>
            Open
          </button>
          <button
            onClick={() => setStatus('done')}
            className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors ${
              status === 'done'
                ? 'bg-gray-200 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Done
          </button>
          <span className="ml-auto self-center text-[10px] text-gray-400 dark:text-gray-500">
            Oldest first
          </span>
        </div>

        {/* Add task — pre-scoped to this room */}
        {addOpen && (
          <AddTaskForm
            orgId={orgId}
            areaId={areaId}
            areaName={areaName}
            orgName={org?.name}
            orgColor={org?.color ?? null}
            onDone={() => setAddOpen(false)}
          />
        )}

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {view.error && view.items.length === 0 && (
            <ErrorState label={view.error} onRetry={handleRetry} />
          )}

          {view.loading && view.items.length === 0 && <ListSkeleton count={3} />}

          {isEmpty &&
            (status === 'open' ? (
              <EmptyState
                icon="check"
                title="Nothing to do here"
                description="Acknowledgements, votes, flagged replies and tasks for this room land here."
                action={{ label: 'Add a task', onClick: () => setAddOpen(true) }}
              />
            ) : (
              <EmptyState icon="check" title="Nothing finished yet" />
            ))}

          {view.items.map((item) => (
            <TodoRow
              key={item.id}
              item={item}
              density="compact"
              /* Every row in this pane is this room — saying so on each is noise. */
              showContext={false}
              currentUserId={currentUserId}
              canAdminOrg={canAdminOrg}
              onOpen={onOpenItem}
            />
          ))}

          {view.hasMore && (
            <div className="p-3 text-center">
              <button
                onClick={handleLoadMore}
                disabled={view.loadingMore}
                className="text-xs font-medium text-blue-600 hover:underline disabled:opacity-50 dark:text-blue-400"
              >
                {view.loadingMore ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================
// Add task
// ============================================================

/**
 * Deliberately minimal: a title, an optional due date, and the audience.
 *
 * No assignee picker — an unassigned task is a ROOM task that anyone in the
 * room can pick up and complete, which is the right default for a pane
 * attached to a room. Assigning is a focused-page job.
 *
 * The audience toggle is not optional polish. Tasks are internal by default
 * (the same rule as v2 comments), so a task created here is invisible to guests
 * in this room unless you say otherwise — and an ambiguous audience is
 * exactly the failure mode that rule exists to prevent, so it is stated on the
 * form in words, using the same `describeTaskAudience` wording as the focused
 * page rather than a second phrasing that could drift from it.
 */
function AddTaskForm({
  orgId,
  areaId,
  areaName,
  orgName,
  orgColor,
  onDone,
}: {
  orgId: string;
  areaId: string;
  areaName: string;
  orgName?: string;
  orgColor: string | null;
  onDone: () => void;
}) {
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = useCallback(() => {
    const trimmed = title.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    createTask({
      orgId,
      areaId,
      title: trimmed,
      // A bare date means end of that day locally, not midnight UTC — otherwise
      // "due today" reads as overdue for anyone west of Greenwich.
      dueAt: dueDate ? new Date(`${dueDate}T23:59:59`).toISOString() : undefined,
      isInternal,
    })
      .then((res) => {
        if (res.task) {
          useTodosStore.getState().applyTask(res.task, { name: orgName, color: orgColor });
        }
        setTitle('');
        setDueDate('');
        toast.success('Task added');
        onDone();
      })
      .catch(() => toast.error('Failed to add task'))
      .finally(() => setSaving(false));
  }, [areaId, dueDate, isInternal, onDone, orgColor, orgId, orgName, saving, title]);

  return (
    <div className="flex-shrink-0 space-y-2 border-b border-gray-200 px-3 py-2.5 dark:border-gray-700">
      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="What needs doing?"
        maxLength={500}
        aria-label="Task title"
        className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          aria-label="Due date (optional)"
          className="flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        />
        <button
          onClick={submit}
          disabled={!title.trim() || saving}
          className="rounded-md bg-blue-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Adding...' : 'Add'}
        </button>
      </div>
      <label className="flex cursor-pointer items-center gap-1.5 py-0.5 text-[11px] text-gray-600 dark:text-gray-300">
        {/* Native control, same as the app's other checkboxes; `accent-blue-600`
            just tints the checked state. 12px was too small a target — the
            whole label is clickable, but the box should be hittable too. */}
        <input
          type="checkbox"
          checked={!isInternal}
          onChange={(e) => setIsInternal(!e.target.checked)}
          className="h-3.5 w-3.5 flex-shrink-0 rounded border-gray-300 accent-blue-600 dark:border-gray-600"
        />
        Guests in this room can see it
      </label>
      <p className="text-[10px] text-gray-500 dark:text-gray-400">
        {describeTaskAudience({ channelName: `#${areaName}`, isInternal })}
      </p>
    </div>
  );
}
